import { lazy, Suspense, useRef, useState, type MouseEvent } from 'react';
import { Outlet } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { useIsClient } from '@/hooks/useIsClient';

// Rare or client-only widgets are their own chunks, so the first load of every
// page carries (and preloads) none of them (audit F8): the drawer is fetched on
// the first tap of the menu button, the app banner and the country suggestion
// after hydration (they render nothing on the server and on the first client
// render anyway).
const MobileDrawer = lazy(() => import('@/components/layout/MobileDrawer'));
const AndroidAppBanner = lazy(() => import('@/components/layout/AndroidAppBanner'));
const CountrySuggestion = lazy(() => import('@/components/layout/CountrySuggestion'));

/** Target of the skip link. */
const MAIN_CONTENT_ID = 'main-content';

/**
 * Top-level layout: skip link, sticky Header, routed page content, Footer.
 * Owns the MobileDrawer open state and hands the opener to the Header.
 */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Mounted from the first open on, so closing it keeps its exit state.
  const [drawerMounted, setDrawerMounted] = useState(false);
  const hydrated = useIsClient();
  const mainRef = useRef<HTMLElement>(null);

  // Move focus ourselves rather than letting the browser follow the fragment:
  // here a hash change is a router navigation (ScrollToTop reacts to it), and it
  // would leave #main-content in a URL people copy and share. The href stays so
  // the link still reads as an in-page link and works if this never runs.
  const skipToContent = (e: MouseEvent<HTMLAnchorElement>) => {
    const main = mainRef.current;
    if (!main) return;
    e.preventDefault();
    main.focus();
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* First tab stop on every page: lets keyboard and screen-reader users jump
          past the header navigation. Visually hidden until focused. */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        onClick={skipToContent}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-xl focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-ink focus:shadow-lg"
      >
        Skip to main content
      </a>
      <Header
        onOpenDrawer={() => {
          setDrawerMounted(true);
          setDrawerOpen(true);
        }}
      />
      {/* tabIndex -1: focusable from the skip link, but not a tab stop. No focus
          ring — a ring around the whole page body is noise; focus moving here
          is what matters to assistive tech. */}
      <main
        ref={mainRef}
        id={MAIN_CONTENT_ID}
        tabIndex={-1}
        className="min-h-page flex-1 outline-none focus-visible:[box-shadow:none]"
      >
        <Outlet />
      </main>
      <Footer />
      {/* One boundary each: the drawer's chunk loading on the first tap must
          not hide a banner already on screen, nor wait for the others. */}
      <Suspense fallback={null}>
        {drawerMounted && <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />}
      </Suspense>
      <Suspense fallback={null}>{hydrated && <AndroidAppBanner />}</Suspense>
      <Suspense fallback={null}>{hydrated && <CountrySuggestion />}</Suspense>
    </div>
  );
}

export default AppShell;
